import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Search,
  Bell,
  Sun,
  Moon,
  Menu,
  LogOut,
  User as UserIcon,
  Settings as SettingsIcon,
  Check,
  AlertTriangle,
  Info,
  CheckCircle2,
} from "lucide-react";
import { useAuth, ROLE_LABELS } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { cn } from "../../lib/utils";
import api from "../../lib/api";
import { formatDistanceToNow } from "date-fns";

function useClickOutside(ref, cb) {
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) cb();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ref, cb]);
}

const kindIcon = {
  critical: AlertTriangle,
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle2,
};
const kindColor = {
  critical: "text-red-600",
  warning: "text-amber-600",
  info: "text-blue-600",
  success: "text-green-600",
};

export default function Navbar({ onMobileMenu }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [openNotif, setOpenNotif] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const notifRef = useRef();
  const profileRef = useRef();
  const [query, setQuery] = useState("");

  useClickOutside(notifRef, () => setOpenNotif(false));
  useClickOutside(profileRef, () => setOpenProfile(false));

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data.items);
      setUnread(data.unread);
    } catch (e) {
      // ignore polling errors silently (avoid console spam every 30s)
      void e;
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    load();
  };
  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  const onSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    // Simple router: /users if role admin, else audits
    navigate(`/audits?q=${encodeURIComponent(query.trim())}`);
  };

  const initials =
    user?.name
      ?.split(" ")
      .map((p) => p[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "U";

  return (
    <header
      data-testid="navbar"
      className="h-16 sticky top-0 z-30 bg-card border-b border-border flex items-center gap-3 px-4 lg:px-6"
    >
      <button
        data-testid="mobile-menu-toggle"
        onClick={onMobileMenu}
        className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-md text-muted-foreground hover:bg-muted"
      >
        <Menu className="w-5 h-5" />
      </button>

      <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-md relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          data-testid="navbar-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search audits, risks, findings…"
          className="h-10 w-full pl-9 pr-3 rounded-md border border-border bg-background text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
        />
      </form>

      <div className="ml-auto flex items-center gap-1">
        <button
          data-testid="theme-toggle"
          onClick={toggle}
          className="inline-flex items-center justify-center w-9 h-9 rounded-md text-muted-foreground hover:bg-muted transition-colors"
          title="Toggle theme"
        >
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            data-testid="notification-bell"
            onClick={() => setOpenNotif((v) => !v)}
            className="relative inline-flex items-center justify-center w-9 h-9 rounded-md text-muted-foreground hover:bg-muted transition-colors"
          >
            <Bell className="w-5 h-5" />
            {unread > 0 && (
              <span
                data-testid="notif-unread-badge"
                className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center"
              >
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>
          {openNotif && (
            <div className="absolute right-0 mt-2 w-96 max-h-[70vh] overflow-hidden rounded-lg border border-border bg-popover shadow-pop animate-fade-in">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div className="text-sm font-semibold">Notifications</div>
                <button
                  data-testid="notif-mark-all-read"
                  onClick={markAllRead}
                  className="text-xs text-primary hover:underline"
                >
                  Mark all read
                </button>
              </div>
              <ul className="max-h-[50vh] overflow-y-auto divide-y divide-border">
                {notifs.length === 0 && (
                  <li className="px-4 py-6 text-sm text-muted-foreground text-center">
                    No notifications yet.
                  </li>
                )}
                {notifs.map((n) => {
                  const Icon = kindIcon[n.kind] || Info;
                  return (
                    <li
                      key={n.id}
                      data-testid={`notif-item-${n.id}`}
                      className={cn(
                        "px-4 py-3 flex gap-3 hover:bg-muted transition-colors cursor-pointer",
                        !n.read && "bg-primary-subtle/40 dark:bg-primary/10"
                      )}
                      onClick={() => !n.read && markOne(n.id)}
                    >
                      <Icon
                        className={cn("w-5 h-5 mt-0.5 shrink-0", kindColor[n.kind] || "text-blue-600")}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{n.title}</div>
                        <div className="text-xs text-muted-foreground line-clamp-2">
                          {n.message}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">
                          {n.created_at && formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                        </div>
                      </div>
                      {!n.read && (
                        <span className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="px-4 py-2 border-t border-border">
                <Link
                  to="/notifications"
                  data-testid="notif-see-all"
                  onClick={() => setOpenNotif(false)}
                  className="text-xs text-primary hover:underline"
                >
                  View all notifications
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Profile menu */}
        <div className="relative" ref={profileRef}>
          <button
            data-testid="profile-menu-trigger"
            onClick={() => setOpenProfile((v) => !v)}
            className="flex items-center gap-2 px-2 h-9 rounded-md hover:bg-muted transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
              {initials}
            </div>
            <div className="hidden sm:flex flex-col items-start leading-tight">
              <span className="text-xs font-semibold text-foreground max-w-[140px] truncate">
                {user?.name}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {ROLE_LABELS[user?.role]}
              </span>
            </div>
          </button>
          {openProfile && (
            <div className="absolute right-0 mt-2 w-56 rounded-lg border border-border bg-popover shadow-pop animate-fade-in overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <div className="text-sm font-semibold truncate">{user?.name}</div>
                <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
              </div>
              <button
                data-testid="menu-profile"
                onClick={() => {
                  setOpenProfile(false);
                  navigate("/profile");
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-muted transition-colors"
              >
                <UserIcon className="w-4 h-4" /> Profile
              </button>
              <button
                data-testid="menu-settings"
                onClick={() => {
                  setOpenProfile(false);
                  navigate("/settings");
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-muted transition-colors"
              >
                <SettingsIcon className="w-4 h-4" /> Settings
              </button>
              <div className="border-t border-border" />
              <button
                data-testid="menu-logout"
                onClick={() => {
                  logout();
                  navigate("/login");
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
              >
                <LogOut className="w-4 h-4" /> Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
